import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import './App.css';

// Layout Components
import DesktopLayout from './layouts/DesktopLayout';
import MobileLayout from './layouts/MobileLayout';

// Page Components
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// UI Components
import ErrorBoundary from './components/ErrorBoundary';
import VoiceAgent from './components/VoiceAgent';
import { onDroneFocus } from './utils/voiceLiveBus';

// Contexts & Hooks
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorLogProvider } from './contexts/ErrorLogContext';
import { useTelemetry } from './hooks/useTelemetry';
import { useDrones } from './hooks/useDrones';

// Config
import { SECTIONS } from './config/constants';

const TABLET_BREAKPOINT = 768;  // Mobile layout for screens < 768px

// Public customer page: ?drop=<share token>. There is no router in this app —
// navigation is a useState string — so the token is read from the query string,
// the same way the API override at config/api.js is. Lazy so mapbox-gl stays
// out of the operator bundle.
const CustomerApp = lazy(() => import('./customer/CustomerApp'));
const DROP_TOKEN = new URLSearchParams(window.location.search).get('drop');
// ?reset=<token>: one-time set-password link from an invite or reset email. Read
// once, then taken out of the address bar so it is not left in history, a
// bookmark or a screenshot; the login card sets the password with it.
const RESET_TOKEN = new URLSearchParams(window.location.search).get('reset');
if (RESET_TOKEN) window.history.replaceState(null, '', window.location.pathname + window.location.hash);

// The active section lives in the URL hash (#missions) so Back/Forward, refresh
// and shared links work without a router. Hash, not path: no server rewrite needed.
const hashOf = (section) => `#${section.toLowerCase()}`;
const sectionFromHash = () =>
  Object.values(SECTIONS).find((s) => hashOf(s) === window.location.hash.toLowerCase()) || null;

/**
 * Main App Component
 */
function App() {
  // Customers have no account, so this branch renders before every provider:
  // no session check, no telemetry socket, no app chrome.
  if (DROP_TOKEN) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div style={{ minHeight: '100dvh', background: '#111319' }} />}>
          <CustomerApp token={DROP_TOKEN} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <NotificationProvider>
          <ErrorLogProvider>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </ErrorLogProvider>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

/**
 * App Content - Main Layout and Routing
 * Routes between desktop and mobile layouts based on screen size
 */
function AppContent() {
  const { toasts, dismissToast, confirmDialog, handleConfirm, showToast } = useNotification();
  const { user, isAuthenticated, isLoading, logout } = useAuth();

  // Auth page toggle: 'login' | 'register'
  const [authPage, setAuthPage] = useState('login');
  const [resetToken, setResetToken] = useState(RESET_TOKEN);

  // Navigation state
  const [activeSection, setActiveSection] = useState(() => sectionFromHash() || SECTIONS.MAP);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    if (window.location.hash.toLowerCase() === hashOf(activeSection)) return;
    // Missing/unknown hash (first load): replace, so Back doesn't stop on a dead entry.
    window.history[sectionFromHash() ? 'pushState' : 'replaceState'](null, '', hashOf(activeSection));
  }, [activeSection]);

  useEffect(() => {
    const onPop = () => setActiveSection(sectionFromHash() || SECTIONS.MAP);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Drones state
  const { drones, reorderDrones, selectedDroneId, setSelectedDroneId, loadDrones } = useDrones();

  // Telemetry state (includes activeMissions from WS)
  const { telemetry, dronesTelemetry, activeMissions } = useTelemetry(activeSection, selectedDroneId);

  // A new CRITICAL server alert (corridor breach, critical battery, telemetry
  // lost, …) is announced wherever the operator is — the Alerts panel only
  // exists on the Drones page. Once per drone and category until it clears.
  const announcedAlertsRef = useRef(new Set());
  useEffect(() => {
    const active = new Set();
    Object.entries(dronesTelemetry || {}).forEach(([droneId, t]) => {
      (t && Array.isArray(t.serverAlerts) ? t.serverAlerts : [])
        .filter((a) => a && a.level === 'CRITICAL')
        .forEach((a) => {
          const key = `${droneId}|${a.category}`;
          active.add(key);
          if (!announcedAlertsRef.current.has(key)) {
            showToast(`${t.droneName || droneId}: ${a.message}`, 'error', 10000);
          }
        });
    });
    announcedAlertsRef.current = active;
  }, [dronesTelemetry, showToast]);

  // "map me drone B pe focus kar" — selecting it is what moves the camera, so
  // the spoken form and the recenter button end up on the same path.
  useEffect(() => onDroneFocus((droneId) => {
    if (droneId) setSelectedDroneId(droneId);
  }), [setSelectedDroneId]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine layout type
  const isMobileLayout = viewportWidth <= TABLET_BREAKPOINT;

  // If auth is still loading (checking stored token), show a loading screen
  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#111319', color: '#94a3b8', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #1e293b', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p>Verifying session...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // If not authenticated, show login or register page. A set-password link shows
  // the login card even over a session: setting the password ends that session.
  if (!isAuthenticated || resetToken) {
    if (authPage === 'register' && !resetToken) {
      return <RegisterPage onSwitchToLogin={() => setAuthPage('login')} />;
    }
    return (
      <LoginPage
        onSwitchToRegister={() => setAuthPage('register')}
        resetToken={resetToken}
        onResetDone={() => { setResetToken(null); if (isAuthenticated) logout(); }}
      />
    );
  }

  // The hash makes #admin typeable, so non-admins fall back to the Map in both layouts.
  const section = activeSection === SECTIONS.ADMIN && user?.role !== 'admin' ? SECTIONS.MAP : activeSection;

  // Render appropriate layout based on device type
  return (
    <>
      {isMobileLayout ? (
        <MobileLayout
          activeSection={section}
          setActiveSection={setActiveSection}
          user={user}
          logout={logout}
          telemetry={telemetry}
          dronesTelemetry={dronesTelemetry}
          activeMissions={activeMissions}
          drones={drones}
          reorderDrones={reorderDrones}
          loadDrones={loadDrones}
          selectedDroneId={selectedDroneId}
          setSelectedDroneId={setSelectedDroneId}
          toasts={toasts}
          dismissToast={dismissToast}
          confirmDialog={confirmDialog}
          handleConfirm={handleConfirm}
        />
      ) : (
        <DesktopLayout
          activeSection={section}
          setActiveSection={setActiveSection}
          user={user}
          logout={logout}
          telemetry={telemetry}
          dronesTelemetry={dronesTelemetry}
          activeMissions={activeMissions}
          drones={drones}
          reorderDrones={reorderDrones}
          loadDrones={loadDrones}
          selectedDroneId={selectedDroneId}
          setSelectedDroneId={setSelectedDroneId}
          toasts={toasts}
          dismissToast={dismissToast}
          confirmDialog={confirmDialog}
          handleConfirm={handleConfirm}
        />
      )}
      <VoiceAgent selectedDroneId={selectedDroneId} dronesTelemetry={dronesTelemetry} />
    </>
  );
}

export default App;

